// types/OmoreInteractionTypes.ts
// TypeScript types for Ask Omore interaction tracking and moderation

import { Timestamp } from 'firebase/firestore';

// ─── Helper Types ────────────────────────────────────────────

export type OmoreHelperType =
    | 'general'
    | 'character'
    | 'setting'
    | 'plot'
    | 'dialogue'
    | 'theme'
    | 'worldBuilding'
    | 'revision'
    | 'brainstorm';

export type OmoreMessageRole = 'user' | 'omore';

export type OmoreOrigin =
    | 'home'
    | 'creatorTools'
    | 'storyCreator'
    | 'characterBuilder'
    | 'settingDesigner'
    | 'plotPlanner';

export type ModerationAction = 'dismissed' | 'warned' | 'blocked';

export type FlagCategory =
    | 'self_harm'
    | 'violence'
    | 'sexual'
    | 'harassment'
    | 'hate'
    | 'inappropriate'
    | 'prompt_injection'
    | 'other';

/** Severity 1–5 mapping for flag categories */
export const FlagSeverityMap: Record<FlagCategory, number> = {
    self_harm: 5,
    violence: 4,
    sexual: 4,
    harassment: 3,
    hate: 3,
    inappropriate: 2,
    prompt_injection: 2,
    other: 1,
};

/** UI colour for each severity level */
export const SeverityColors: Record<number, string> = {
    5: '#dc2626',
    4: '#ef4444',
    3: '#f59e0b',
    2: '#eab308',
    1: '#9ca3af',
};

// ─── Session & Message ───────────────────────────────────────

/**
 * A single Omore conversation session
 * Stored in: omoreInteractions/sessions/items/{sessionId}
 */
export interface OmoreSession {
    id: string;
    appleID: string;
    profileID: string;
    explorerName: string;
    helperType: OmoreHelperType;
    origin: OmoreOrigin;
    startedAt: Timestamp;
    endedAt?: Timestamp;
    durationSeconds: number;
    messageCount: number;
    totalTokensUsed: number;
    suggestionsShown: number;
    suggestionsAdopted: number;
    hasModerationFlag: boolean;
    deviceType: 'iPhone' | 'iPad';
    appVersion: string;
}

/**
 * A single message inside a Omore session
 * Stored in: omoreInteractions/messages/items/{messageId}
 */
export interface OmoreMessage {
    id: string;
    sessionID: string;
    role: OmoreMessageRole;
    content: string;
    timestamp: Timestamp;
    tokenCount: number;
    isSuggestion: boolean;
    suggestionAdopted?: boolean;
    moderationFlags?: FlagCategory[];
    moderationSeverity?: number;
}

// ─── Moderation Queue ────────────────────────────────────────

/**
 * Flagged message entry in the moderation queue
 * Stored in: omoreInteractions/moderation/flagged/{messageId}
 */
export interface FlaggedMessage {
    id: string;
    sessionID: string;
    appleID: string;
    profileID: string;
    explorerName: string;
    helperType: OmoreHelperType;
    origin: OmoreOrigin;
    messageContent: string;
    flagCategories: FlagCategory[];
    maxSeverity: number;
    flaggedAt: Timestamp;
    reviewedAt?: Timestamp;
    reviewedBy?: string;
    reviewAction?: ModerationAction;
    reviewNotes?: string;
}

/**
 * Filter options for the moderation queue
 */
export interface ModerationQueueFilter {
    reviewStatus?: 'unreviewed' | 'reviewed' | 'all';
    categories?: FlagCategory[];
    startDate?: string;
    endDate?: string;
    sortBy?: 'flaggedAt' | 'maxSeverity';
    sortDirection?: 'asc' | 'desc';
    page?: number;
    pageSize?: number;
}

/**
 * Paginated response from the moderation queue
 */
export interface PaginatedFlaggedResponse {
    items: FlaggedMessage[];
    totalCount: number;
    totalPages: number;
    currentPage: number;
}

// ─── User Abuse Summary ──────────────────────────────────────

/**
 * Aggregate abuse summary for a single user
 * Stored in: omoreInteractions/moderation/users/{appleId}
 */
export interface UserAbuseSummary {
    appleID: string;
    profileID: string;
    explorerName: string;
    totalFlags: number;
    flagsByCategory: Record<string, number>;
    highestSeverity: number;
    firstFlaggedAt: Timestamp;
    lastFlaggedAt: Timestamp;
    isBlocked: boolean;
    blockedAt?: Timestamp;
    blockedReason?: string;
    blockedBy?: string;
    sampleMessageIDs: string[];
}

// ─── Analytics ───────────────────────────────────────────────

/**
 * Daily aggregated Omore-specific analytics
 * Stored in: omoreInteractions/analytics/daily/{date}
 */
export interface OmoreInteractionDailyAnalytics {
    date: string;
    totalSessions: number;
    totalMessages: number;
    uniqueUsers: number;
    sessionsByHelperType: Record<string, number>;
    sessionsByOrigin: Record<string, number>;
    totalSuggestionsShown: number;
    totalSuggestionsAdopted: number;
    avgSessionDurationSeconds: number;
    totalTokensUsed: number;
    flaggedMessageCount: number;
}

/**
 * Aggregated Omore analytics across a date range (computed client-side)
 */
export interface OmoreInteractionAggregatedAnalytics {
    startDate: string;
    endDate: string;
    totalSessions: number;
    totalMessages: number;
    uniqueUsers: number;
    suggestionAdoptionRate: number;
    avgSessionDurationSeconds: number;
    sessionsByHelperType: Record<string, number>;
    sessionsByOrigin: Record<string, number>;
    dailyStats: OmoreInteractionDailyAnalytics[];
}

/**
 * Filter for Omore analytics queries
 */
export interface OmoreAnalyticsFilter {
    startDate: string;
    endDate: string;
    helperType?: OmoreHelperType;
    origin?: OmoreOrigin;
}

// ─── Session Detail (client-side assembled) ─────────────────

/**
 * Full session detail with messages, used by OmoreSessionDetail component
 */
export interface OmoreSessionDetail {
    session: OmoreSession;
    messages: OmoreMessage[];
    explorerName: string;
}
